import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../../Redux/Store';
import { isEntitled } from '../../../Utils/ContentOrConfigUtil';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import {
  TSearchQueryError,
  clearGlobalSearch,
  getSearchLoads,
  updateShowSearchResult,
  handleResetSearchQuery,
  handleResetSearchQueryError,
  handleResetSortAndFilter,
  updateFavoriteSearch,
  updateSearchQuery,
  updateSearchQueryError,
  handleResetPaginationFilter,
  handleResetToggleFilter,
} from '../../../Redux/Reducers/SearchSlice';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import { isEmptyObject, objectValuesAreEmptyStrings, scrollIntoView } from '../../../Utils/Basic';
import {
  commonDateFormat,
  dataBaseFriendlyDateFormat,
  dayjs,
  isBeforeFirstDate,
} from '../../../Utils/DateUtils';
import {
  ANYWHERE,
  CANCEL_TEXT,
  COUNTRY,
  FAVORITE_SEARCH_ADDED,
  FAVORITE_SEARCH_REMOVED,
  MinMaxErrorMsg,
  SELECT_SEARCH_WARNING_TEXT,
  SERVER_ERROR_MESSAGES,
  US_TIME_ZONE,
  dropDateOffset,
  pickupDateOffset,
} from '../../../Constants/Constants';
import { EVENT, LEAD, MODE, SCREEN, TAB } from '../../../Constants/AnalyticsConstants';
import ResultContainer from '../../ResultContainer/ResultContainer';
import NewSearchSection from './NewSearchSection/NewSearchSection';
import AdvanceSearchComponent from '../../PreferenceComponent/UtilComponents/AdvanceSearchComponent/AdvanceSearchComponent';
import './NewSearchContainer.scss';
import ToggleSearchButton from '../../Shared/ToggleSearchButton/ToggleSearchButton';
import CustomModal from '../../Shared/CustomModal/CustomModal';

const NewSearchContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isMobile } = useContext(AppContext);
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { preferencesData } = useAppSelector((state: RootState) => state.Preferences);
  const {
    configTableData,
    configTableData: {
      FPO_APP_CONFIG: { APP_INIT, PREFERENCES_INIT } = { PREFERENCES_INIT: [], APP_INIT: {} },
    } = {},
  } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const appConfigData = configTableData?.FPO_APP_CONFIG;
  const {
    searchQuery,
    searchQueryError,
    searchResultData,
    sortAndFilter,
    searchById,
    loadByIdData,
    showSearchResult,
  } = useAppSelector((state: RootState) => state?.search);

  const [isFavorite, setIsFavorite] = useState(false);
  const [isDestinationExpanded, setIsDestinationExpanded] = useState(false);
  const [isStateSelectionEnabled, setIsStateSelectionEnabled] = useState(false);
  const [userInteractionWithDestination, setUserInteractionWithDestination] = useState(false);
  const [initialRadius, setInitialRadius] = useState<any>(null);
  const [userInteractedPayload, setUserInteractedPayload] = useState<any>({});
  const [isAdvancedFilterToggledOn, setIsAdvancedFilterToggledOn] = useState(false);
  const [advanceFilterModalProps, setAdvanceFilterModalProps] = useState<any>({
    isModalOpen: false,
  });
  const [temporaryUserInteractedPayload, setTemporaryUserInteractedPayload] = useState<any>({});
  const [maxMilesError, setMaxMilesError] = useState<any>('');

  useEffect(() => {
    if (!isEmptyObject(state) && state?.isReload) {
      isStateSelectionEnabled && setIsStateSelectionEnabled(false);
      userInteractionWithDestination && setUserInteractionWithDestination(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  useEffect(() => {
    if (state?.isEdit) {
      if (!!searchQuery?.maxMiles || !!searchQuery?.minMiles || !!searchQuery?.minRPM) {
        const tempPayload = {
          excludeSpecialServices: '[]',
          minRPM: parseFloat(searchQuery?.minRPM)
            ? parseFloat(searchQuery?.minRPM)?.toFixed(2)?.toString()
            : 'null',
          minMiles: searchQuery?.minMiles?.toString(),
          maxMiles: searchQuery?.maxMiles?.toString(),
        };
        const _payload = {
          preferredLoadExclusion: '[]',
          preferredmaxmiles: tempPayload?.maxMiles,
          preferredminmiles: tempPayload?.minMiles,
          preferredminrpm: tempPayload?.minRPM,
        };
        dispatch(updateSearchQuery({ ...searchQuery, ...tempPayload }));
        setUserInteractedPayload(_payload);
        setIsAdvancedFilterToggledOn(true);
      }
      state.isEdit = false;
    }
  }, [state, state?.isEdit]);
  useEffect(() => {
    if (searchResultData?.loadPostings || showSearchResult) {
      scrollIntoView('ResultContainer');
    }
    return () => {
      dispatch(clearGlobalSearch());
    };
  }, [searchResultData?.loadPostings, showSearchResult]);

  useEffect(() => {
    if (
      (!searchQuery?.originRadius || !searchQuery?.destinationRadius || !initialRadius) &&
      appConfigData
    ) {
      let temp = { ...initialRadius };
      if (appConfigData?.ORIGIN_RADIUS_INIT && isEmptyObject(searchQuery?.originLocation)) {
        temp = { ...temp, originRadius: appConfigData?.ORIGIN_RADIUS_INIT?.initialValue };
      }
      if (
        appConfigData?.DESTINATION_RADIUS_INIT &&
        isEmptyObject(searchQuery?.destinationLocation)
      ) {
        temp = { ...temp, destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue };
      }
      setInitialRadius(temp);
      dispatch(updateSearchQuery({ ...searchQuery, ...temp }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appConfigData, searchQuery?.originRadius, searchQuery?.destinationRadius]);

  useEffect(() => {
    if (
      !isStateSelectionEnabled &&
      !isDestinationExpanded &&
      Array.isArray(searchQuery?.destinationLocation?.State)
    ) {
      setIsStateSelectionEnabled(true);
      setIsDestinationExpanded(true);
    }
  }, [searchQuery?.destinationLocation.State]);

  const handleFavorite = () => {
    if (
      isEmptyObject(searchQuery?.originLocation) &&
      isEmptyObject(searchQuery?.destinationLocation)
    ) {
      return toast(SELECT_SEARCH_WARNING_TEXT);
    }

    if (!isFavorite) {
      let queryPayloads = {
        userId: memberData?.member?.upn ?? '',
        carrierId: memberData?.member?.userApplicationDetails?.[0].attributes.CommonCarrierId ?? '',
        favouriteSearchIndicator: true,
        originCity: searchQuery?.originLocation?.City ?? ANYWHERE,
        originState: searchQuery?.originLocation?.State ?? '',
        originRadius: searchQuery?.originRadius,
        originCountry: searchQuery?.originLocation?.Country ?? 'US',
        originPostalCode: searchQuery?.originLocation?.Zip ?? '',
        pickupStartDate: dayjs(searchQuery?.originFromDate).tz(US_TIME_ZONE).toISOString(),
        pickupEndDate: dayjs(searchQuery?.originToDate).tz(US_TIME_ZONE).toISOString(),
        dropStartDate: dayjs(searchQuery?.destinationFromDate).tz(US_TIME_ZONE).toISOString(),
        dropEndDate: dayjs(searchQuery?.destinationToDate).tz(US_TIME_ZONE).toISOString(),
        destinationCity: !isStateSelectionEnabled
          ? (searchQuery?.destinationLocation?.City ?? ANYWHERE)
          : '',
        destinationState: null,
        destinationState1: isStateSelectionEnabled
          ? searchQuery?.destinationLocation?.State?.[0]?.code
          : searchQuery?.destinationLocation?.State?.[0],
        destinationState2:
          isStateSelectionEnabled && !isEmptyObject(searchQuery?.destinationLocation?.State?.[1])
            ? searchQuery?.destinationLocation?.State?.[1]?.code
            : null,
        destinationState3:
          isStateSelectionEnabled && !isEmptyObject(searchQuery?.destinationLocation?.State?.[2])
            ? searchQuery?.destinationLocation?.State?.[2]?.code
            : null,
        destinationCountry: searchQuery?.destinationLocation?.Country ?? '',
        destinationPostalCode: searchQuery?.destinationLocation?.Zip ?? '',
        destinationRadius: searchQuery?.destinationRadius,
        dropDateOffset: dropDateOffset,
        durationDays: null,
        pickupDateOffset: pickupDateOffset,
        searchDate: new Date().toISOString(),
        timeZone: US_TIME_ZONE,
        trailerType: 'Power Only',
        pickUpDate: null,
        siteId: 'FPO',
      };
      const callback = (res: any) => {
        if (res?.status) {
          toast(
            queryPayloads?.favouriteSearchIndicator
              ? FAVORITE_SEARCH_ADDED
              : FAVORITE_SEARCH_REMOVED,
          );
          setIsFavorite(!isFavorite);
        } else {
          toast(res?.errorMessage ?? SERVER_ERROR_MESSAGES?.generalMsg);
        }
      };
      if (isAdvancedFilterToggledOn) {
        const advancedSearchExtraPayload = {
          excludeSpecialServices:
            (userInteractedPayload?.preferredLoadExclusion ||
              searchQuery?.excludeSpecialServices) === '[]'
              ? []
              : userInteractedPayload?.preferredLoadExclusion ||
                searchQuery?.excludeSpecialServices,
          maxMiles: userInteractedPayload?.preferredmaxmiles || searchQuery?.maxMiles,
          minMiles: userInteractedPayload?.preferredminmiles || searchQuery?.minMiles,
          minRPM: userInteractedPayload?.preferredminrpm || searchQuery?.minRPM,
        };
        queryPayloads = { ...queryPayloads, ...advancedSearchExtraPayload };
        updateSearchQueryWithAdvanceFilterData(advancedSearchExtraPayload);
      }
      dispatch(updateFavoriteSearch({ queryPayloads, callback }));
      fbLogEvent(EVENT.UE_MARK_FAVORITE, {
        screen: SCREEN.SEARCH,
        tab: TAB.NEW,
        origin: `${(searchQuery?.originLocation?.City ?? ANYWHERE) + '_'}${
          searchQuery?.originLocation?.State ?? ''
        }`,
        destination: `${
          !isStateSelectionEnabled ? (searchQuery?.destinationLocation?.City ?? ANYWHERE) + '_' : ''
        }${
          isStateSelectionEnabled
            ? searchQuery?.destinationLocation?.State?.filter(
                (item?: any) => !isEmptyObject(item),
              ).map((item: any) => item?.code)
            : searchQuery?.destinationLocation?.State
              ? [searchQuery?.destinationLocation?.State]
              : []
        }`,
      });
    }
  };
  const handleNewSearch = (_page?: any) => {
    let payload: any = {
      size: sortAndFilter?.size,
      page: _page !== undefined ? _page : sortAndFilter?.page,
      sortColumn: sortAndFilter?.sortColumn,
      sortDirection: sortAndFilter?.sortDirection,
      originCity: searchQuery?.originLocation?.City ?? ANYWHERE,
      originState: searchQuery?.originLocation?.State ?? '',
      originRadius: searchQuery?.originRadius,
      originCountry: searchQuery?.originLocation?.Country ?? 'US',
      originPostalCode: searchQuery?.originLocation?.Zip ?? '',
      pickUpStartDate:
        dataBaseFriendlyDateFormat(searchQuery?.originFromDate, 'startDate', 'GMT') ?? null,
      pickUpEndDate:
        dataBaseFriendlyDateFormat(searchQuery?.originToDate, 'endDate', 'GMT') ?? null,
      dropStartDate:
        userInteractionWithDestination || searchQuery?.destinationFromDate
          ? dataBaseFriendlyDateFormat(searchQuery?.destinationFromDate, 'startDate', 'GMT') || ''
          : '',
      dropEndDate:
        userInteractionWithDestination || searchQuery?.destinationToDate
          ? dataBaseFriendlyDateFormat(searchQuery?.destinationToDate, 'endDate', 'GMT') || ''
          : '',
      destinationCity: !isStateSelectionEnabled
        ? (searchQuery?.destinationLocation?.City ?? ANYWHERE)
        : '',
      destinationStates: isStateSelectionEnabled
        ? searchQuery?.destinationLocation?.State?.filter((item?: any) => !isEmptyObject(item)).map(
            (item: any) => item?.code,
          )
        : searchQuery?.destinationLocation?.State
          ? [searchQuery?.destinationLocation?.State]
          : [],
      destinationCountry: searchQuery?.destinationLocation?.Country ?? '',
      destinationPostalCode: searchQuery?.destinationLocation?.Zip ?? '',
      destinationRadius: searchQuery?.destinationRadius,
      appointmentConfirmed: null,
      extendedNetwork: null,
    };
    if (isAdvancedFilterToggledOn) {
      const advancedSearchExtraPayload = {
        excludeSpecialServices:
          (userInteractedPayload?.preferredLoadExclusion || searchQuery?.excludeSpecialServices) ===
          '[]'
            ? []
            : userInteractedPayload?.preferredLoadExclusion || searchQuery?.excludeSpecialServices,
        maxMiles: userInteractedPayload?.preferredmaxmiles || searchQuery?.maxMiles,
        minMiles: userInteractedPayload?.preferredminmiles || searchQuery?.minMiles,
        minRPM: userInteractedPayload?.preferredminrpm || searchQuery?.minRPM,
      };
      payload = { ...payload, ...advancedSearchExtraPayload };
      updateSearchQueryWithAdvanceFilterData(advancedSearchExtraPayload);
    }
    dispatch(getSearchLoads({ queryPayloads: payload }));
  };

  useEffect(() => {
    const temp: TSearchQueryError = { ...searchQueryError };
    if (isBeforeFirstDate(searchQuery?.originFromDate, searchQuery?.originToDate)) {
      temp.originToDate = 'Please provide a correct pick up date';
    } else {
      temp.originToDate = '';
    }
    if (isBeforeFirstDate(searchQuery?.destinationFromDate, searchQuery?.destinationToDate)) {
      temp.destinationToDate = 'Please provide a correct delivery date';
    } else {
      if (
        isBeforeFirstDate(searchQuery?.originToDate, searchQuery?.destinationToDate) &&
        userInteractionWithDestination
      ) {
        temp.destinationToDate = 'Delivery Date Range cannot begin earlier than Pick-Up Date Range';
      } else {
        temp.destinationToDate = '';
      }
    }
    if (
      isBeforeFirstDate(searchQuery?.originFromDate, searchQuery?.destinationFromDate) &&
      userInteractionWithDestination
    ) {
      temp.destinationFromDate = 'Delivery Date Range cannot begin earlier than Pick-Up Date Range';
    } else {
      temp.destinationFromDate = '';
    }
    dispatch(updateSearchQueryError({ ...temp }));
  }, [dispatch, searchQuery, userInteractionWithDestination]);

  useEffect(() => {
    dispatch(updateShowSearchResult(false));
  }, []);

  const handleLocationChange = (type: string, data: any) => {
    if (type === 'destination' && !userInteractionWithDestination) {
      setUserInteractionWithDestination(true);
    }
    let temp = { ...searchQuery, [`${type}Location`]: data };
    if (type === 'destination' && isEmptyObject(data)) {
      temp = { ...temp, destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue };
    }
    dispatch(handleResetPaginationFilter());
    dispatch(updateSearchQuery({ ...temp }));
  };
  const handleRadiusChange = (data: any) => {
    dispatch(handleResetPaginationFilter());
    return dispatch(updateSearchQuery({ ...searchQuery, [data?.name]: data?.value }));
  };
  const handleClearAll = () => {
    !!isStateSelectionEnabled && setIsStateSelectionEnabled(false);
    !!userInteractionWithDestination && setUserInteractionWithDestination(false);
    !!showSearchResult && dispatch(updateShowSearchResult(false));
    setUserInteractedPayload({});
    setTemporaryUserInteractedPayload({});
    isAdvancedFilterToggledOn && setIsAdvancedFilterToggledOn(false);
    dispatch(handleResetSearchQuery());
    dispatch(handleResetSortAndFilter());
    dispatch(handleResetSearchQueryError());
  };
  const handleReset = (type: string) => {
    if (type === 'destination') {
      !!isStateSelectionEnabled && setIsStateSelectionEnabled(false);
      !!userInteractionWithDestination && setUserInteractionWithDestination(false);
    }
    dispatch(
      updateSearchQuery({
        ...searchQuery,
        [`${type}Location`]: {},
        [`${type}Radius`]: initialRadius[`${type}Radius`] || 0,
        [`${type}FromDate`]: commonDateFormat(dayjs().add(0, 'day')) as string,
        [`${type}ToDate`]: commonDateFormat(
          dayjs().add(type === 'origin' ? 3 : 10, 'day'),
        ) as string,
      }),
    );
    dispatch(
      updateSearchQueryError({
        ...searchQueryError,
        [`${type}Location`]: '',
        [`${type}FromDate`]: '',
        [`${type}ToDate`]: '',
      }),
    );
  };
  const handleDateChange = (type: string, data: any) => {
    if (type?.includes('destination') && !userInteractionWithDestination) {
      setUserInteractionWithDestination(true);
    }
    dispatch(
      updateSearchQueryError({
        ...searchQueryError,
        [`${type}`]: '',
      }),
    );
    dispatch(handleResetSortAndFilter());
    dispatch(updateSearchQuery({ ...searchQuery, [type]: commonDateFormat(data) }));
  };
  const handleDestinationExpansion = (value: boolean) => {
    setIsDestinationExpanded(value);
  };
  const handleStateSelector = () => {
    if (isStateSelectionEnabled) {
      dispatch(
        updateSearchQuery({
          ...searchQuery,
          destinationLocation: {},
          destinationRadius: initialRadius.destinationRadius || 0,
        }),
      );
    } else {
      dispatch(
        updateSearchQuery({
          ...searchQuery,
          destinationLocation: {
            CountryFullName: COUNTRY[0]?.CountryFullName,
            Country: COUNTRY[0]?.Country,
            State: [{}, {}, {}],
            destinationRadius: initialRadius.destinationRadius || 0,
          },
        }),
      );
    }
    setIsStateSelectionEnabled(!isStateSelectionEnabled);
    !userInteractionWithDestination && setUserInteractionWithDestination(true);
  };
  const handleResultsClick = () => {
    dispatch(handleResetToggleFilter(true));
    if (!objectValuesAreEmptyStrings({ ...searchQueryError })) return null;
    if (
      isEmptyObject(searchQuery?.originLocation) &&
      (isEmptyObject(searchQuery?.destinationLocation) ??
        (isStateSelectionEnabled &&
          !searchQuery?.destinationLocation?.State?.filter((item: any) => !isEmptyObject(item))
            ?.length))
    )
      return toast(SELECT_SEARCH_WARNING_TEXT);
    dispatch(handleResetPaginationFilter());
    handleNewSearch(0);
    fbLogEvent(EVENT.UE_SEARCH_RESULTS, {
      screen: SCREEN.SEARCH,
      tab: TAB.NEW,
      origin: `${(searchQuery?.originLocation?.City ?? ANYWHERE) + '_'}${
        searchQuery?.originLocation?.State ?? ''
      }`,
      destination: `${
        !isStateSelectionEnabled ? (searchQuery?.destinationLocation?.City ?? ANYWHERE) + '_' : ''
      }${
        isStateSelectionEnabled
          ? searchQuery?.destinationLocation?.State?.filter(
              (item?: any) => !isEmptyObject(item),
            ).map((item: any) => item?.code)
          : searchQuery?.destinationLocation?.State
            ? [searchQuery?.destinationLocation?.State]
            : []
      }`,
    });
    if (isMobile)
      return navigate('/search/results', {
        state: {
          favouriteSearchIndicator: isFavorite,
          userInteractionWithDestination: userInteractionWithDestination,
          isStateSelectionEnabled: isStateSelectionEnabled,
        },
      });
    dispatch(updateShowSearchResult(true));
    !!userInteractionWithDestination && setIsDestinationExpanded(false);
  };
  const handleChange = (type: string, slug: string, value: string, defaultValue: string = '') => {
    setTemporaryUserInteractedPayload((prevState: any) => {
      switch (type) {
        case 'dropdown': {
          const updatedState = { ...prevState, [slug]: value };
          if (slug === 'preferredminmiles' || slug === 'preferredmaxmiles') {
            const minMiles = parseInt(updatedState.preferredminmiles);
            const maxMiles = parseInt(updatedState.preferredmaxmiles);
            if (maxMiles > minMiles) {
              setMaxMilesError('');
            } else {
              setMaxMilesError(MinMaxErrorMsg);
            }
          }
          return updatedState;
        }
        case 'checkbox': {
          let updatedValue: string;
          const currentValues = (prevState[slug] || defaultValue)?.split(',') || [];
          if (currentValues.includes(value)) {
            updatedValue =
              currentValues.filter((item: string) => item !== value).join(',') || 'none';
          } else {
            updatedValue = [
              ...currentValues.filter((item: string) => item !== 'none' && !!item),
              value,
            ].join(',');
          }
          return { ...prevState, [slug]: updatedValue };
        }
        default:
          return prevState;
      }
    });
  };
  const populatePreferredAdvanceFilterData = () => {
    const advancedSearchConfig = PREFERENCES_INIT?.find(
      (item: any) => item?.ACCORD_ROLE_FILTER_KEY === 'advancedSearch',
    )?.ACCORD_CONTENT;
    const data = advancedSearchConfig?.reduce((acc: any, item: any) => {
      if (item?.API_KEY === 'preferredLoadExclusion') {
        const _value = preferencesData?.find((_item: any) => _item?.key === item?.API_KEY)?.value;
        acc[item?.API_KEY] =
          (_value?.preferredLoadExclusion?.includes('[') ? JSON.parse(_value) : _value) ||
          item?.DEFAULT_VALUE ||
          [];
      } else {
        const _value = preferencesData?.find((_item: any) => _item?.key === item?.API_KEY)?.value;
        acc[item?.API_KEY] = _value || item?.DEFAULT_VALUE;
      }
      return acc;
    }, {});
    return data;
  };
  const handleAdvanceSearchReset = () => {
    const _Data = populatePreferredAdvanceFilterData();
    if (parseInt(_Data?.preferredmaxmiles) > parseInt(_Data?.preferredminmiles)) {
      setMaxMilesError('');
    } else {
      setMaxMilesError(MinMaxErrorMsg);
    }
    setTemporaryUserInteractedPayload(_Data);
    fbLogEvent(EVENT.UE_ADVANCED_FILTERS_RESET, {
      screen: SCREEN.ADVANCED_FILTERS_DETAILS,
      lead: LEAD.NEW,
      mode: isAdvancedFilterToggledOn ? MODE.ADVANCED_FILTERS_ON : MODE.ADVANCED_FILTERS_OFF,
      services_exclusion: Array.isArray(_Data?.preferredLoadExclusion)
        ? _Data.preferredLoadExclusion.join(', ')
        : typeof _Data?.preferredLoadExclusion === 'string'
          ? _Data.preferredLoadExclusion
          : '[]',
      min_loaded_rpm: _Data?.preferredminrpm || 'null',
      min_loaded_distance: _Data?.preferredminmiles || 'null',
      max_loaded_distance: _Data?.preferredmaxmiles || 'null',
    });
  };
  const resetButton = (
    <button className="Reset_Btn" onClick={() => handleAdvanceSearchReset()}>
      <img src="/oo/assets/icon/refresh.svg" alt="refresh" className="mr-2" />
      {!isMobile && <span>Reset</span>}
    </button>
  );
  const handleClickExpandAdvance = () => {
    const _Data = isEmptyObject(userInteractedPayload)
      ? populatePreferredAdvanceFilterData()
      : userInteractedPayload;
    setTemporaryUserInteractedPayload(_Data);
    if (parseInt(_Data?.preferredmaxmiles) > parseInt(_Data?.preferredminmiles)) {
      setMaxMilesError('');
    } else {
      setMaxMilesError(MinMaxErrorMsg);
    }
    setAdvanceFilterModalProps({
      isModalOpen: true,
      titleIcon: '/oo/assets/icon/info.svg',
      additionalTitleOptions: resetButton,
      contentAlignment: 'left',
      additionalCssClass: 'AdvanceSearchFilterModal',
      contentScroll: 'deny',
    });

    fbLogEvent(EVENT.UE_ADVANCE_SEARCH, {
      screen: SCREEN.SEARCH,
      tab: TAB.NEW,
      mode: isAdvancedFilterToggledOn ? MODE.ADVANCE_SEARCH_OFF : MODE.ADVANCE_SEARCH_ON,
    });
    fbLogEvent(EVENT.UE_ADVANCED_FILTERS_DETAILS, {
      screen: SCREEN.ADVANCED_FILTERS_DETAILS,
      lead: LEAD.NEW,
      mode: isAdvancedFilterToggledOn ? MODE.ADVANCED_FILTERS_ON : MODE.ADVANCED_FILTERS_OFF,
      services_exclusion: Array.isArray(_Data?.preferredLoadExclusion)
        ? _Data.preferredLoadExclusion.join(', ')
        : typeof _Data?.preferredLoadExclusion === 'string'
          ? _Data.preferredLoadExclusion
          : '[]',
      min_loaded_rpm: _Data?.preferredminrpm || 'null',
      min_loaded_distance: _Data?.preferredminmiles || 'null',
      max_loaded_distance: _Data?.preferredmaxmiles || 'null',
    });
  };
  const updateSearchQueryWithAdvanceFilterData = (extraPayload?: any) => {
    const { excludeSpecialServices, minRPM, maxMiles, minMiles, ...restSearchQuery } = searchQuery;

    const updatedQuery = { ...restSearchQuery };

    if (extraPayload) {
      Object.assign(updatedQuery, extraPayload);
    }

    dispatch(updateSearchQuery(updatedQuery));
  };
  const handleAcceptAdvanceSearch = () => {
    const _Data = { ...temporaryUserInteractedPayload };
    if (parseInt(_Data?.preferredmaxmiles) < parseInt(_Data?.preferredminmiles)) {
      return setMaxMilesError(MinMaxErrorMsg);
    }
    setUserInteractedPayload(_Data);
    setAdvanceFilterModalProps({ isModalOpen: false });
    setTemporaryUserInteractedPayload({});
    !isAdvancedFilterToggledOn && setIsAdvancedFilterToggledOn(true);
    const advancedSearchExtraPayload = {
      excludeSpecialServices:
        _Data?.preferredLoadExclusion === '[]' ? [] : _Data?.preferredLoadExclusion,
      maxMiles: _Data?.preferredmaxmiles,
      minMiles: _Data?.preferredminmiles,
      minRPM: _Data?.preferredminrpm,
    };
    updateSearchQueryWithAdvanceFilterData(advancedSearchExtraPayload);
    maxMilesError && setMaxMilesError('');
    fbLogEvent(EVENT.UE_ADVANCED_FILTERS_ACCEPT, {
      screen: SCREEN.ADVANCED_FILTERS_DETAILS,
      lead: LEAD.NEW,
      mode: isAdvancedFilterToggledOn ? MODE.ADVANCED_FILTERS_ON : MODE.ADVANCED_FILTERS_OFF,
      services_exclusion: Array.isArray(_Data?.preferredLoadExclusion)
        ? _Data.preferredLoadExclusion.join(', ')
        : typeof _Data?.preferredLoadExclusion === 'string'
          ? _Data.preferredLoadExclusion
          : '[]',
      min_loaded_rpm: _Data?.preferredminrpm || 'null',
      min_loaded_distance: _Data?.preferredminmiles || 'null',
      max_loaded_distance: _Data?.preferredmaxmiles || 'null',
    });
  };

  const handleCancelAdvanceSearch = () => {
    const _Data = isEmptyObject(userInteractedPayload)
      ? populatePreferredAdvanceFilterData()
      : userInteractedPayload;
    setAdvanceFilterModalProps({ isModalOpen: false });
    setTemporaryUserInteractedPayload(_Data);
    if (isEmptyObject(userInteractedPayload)) {
      setIsAdvancedFilterToggledOn(false);
    }
    fbLogEvent(EVENT.UE_ADVANCED_FILTERS_CANCEL, {
      screen: SCREEN.ADVANCED_FILTERS_DETAILS,
      lead: LEAD.NEW,
      mode: isAdvancedFilterToggledOn ? MODE.ADVANCED_FILTERS_ON : MODE.ADVANCED_FILTERS_OFF,
      services_exclusion: Array.isArray(_Data?.preferredLoadExclusion)
        ? _Data.preferredLoadExclusion.join(', ')
        : typeof _Data?.preferredLoadExclusion === 'string'
          ? _Data.preferredLoadExclusion
          : '[]',
      min_loaded_rpm: _Data?.preferredminrpm || 'null',
      min_loaded_distance: _Data?.preferredminmiles || 'null',
      max_loaded_distance: _Data?.preferredmaxmiles || 'null',
    });
  };

  return (
    <div className="New_Search_Container">
      {!searchById && (
        <div className="New_Search_Section">
          <div className="Search_Section_Container">
            {!isMobile && (
              <div className="Search_Section_Left">
                <div>
                  <img src="/oo/assets/icon/origin_icon.svg" alt="origin_icon" />
                  <span>Origin</span>
                </div>
                <div>
                  <img
                    src="/oo/assets/icon/destination_icon.svg"
                    alt="destination_icon"
                    className="destination_icon"
                  />
                  <span>Destination</span>
                </div>
              </div>
            )}
            <div className="Search_Section_Right">
              <NewSearchSection
                type="origin"
                data={searchQuery}
                handleReset={handleReset}
                handleLocationChange={handleLocationChange}
                handleRadiusChange={handleRadiusChange}
                handleDateChange={handleDateChange}
                handleDestinationExpansion={handleDestinationExpansion}
              />
              <NewSearchSection
                type="destination"
                data={searchQuery}
                isDestinationExpanded={isDestinationExpanded}
                isStateSelectionEnabled={isStateSelectionEnabled}
                handleReset={handleReset}
                handleLocationChange={handleLocationChange}
                handleRadiusChange={handleRadiusChange}
                handleDateChange={handleDateChange}
                handleDestinationExpansion={handleDestinationExpansion}
                handleStateSelector={handleStateSelector}
                userInteractionWithDestination={userInteractionWithDestination}
                handleInteractionWithDestination={() => setUserInteractionWithDestination(true)}
              />
            </div>
          </div>
          {PREFERENCES_INIT?.filter((item: any) =>
            isEntitled(memberData?.member, APP_INIT, 'preferences', item?.ACCORD_ROLE_FILTER_KEY),
          )?.map((accord: any, index: number) => {
            if (accord?.ACCORD_ROLE_FILTER_KEY !== 'advancedSearch') return null;
            const userAlteredData = accord?.ACCORD_CONTENT?.reduce((acc: any, item: any) => {
              if (userInteractedPayload[item?.API_KEY]) {
                acc[item?.API_KEY] = userInteractedPayload[item?.API_KEY];
              }
              return acc;
            }, {});
            return (
              <div className="AdvanceSearch_Section" key={index}>
                <div className="Advanced_Header">
                  <div className="AdvanceSearch_Title">
                    <p>{accord?.ACCORD_TITLE}</p>
                    <ToggleSearchButton
                      onToggle={() => {
                        if (!isAdvancedFilterToggledOn) {
                          isEmptyObject(userInteractedPayload) &&
                            setUserInteractedPayload(populatePreferredAdvanceFilterData());
                        } else {
                          setTemporaryUserInteractedPayload({});
                          setUserInteractedPayload({});
                          updateSearchQueryWithAdvanceFilterData();
                        }
                        setIsAdvancedFilterToggledOn(!isAdvancedFilterToggledOn);
                        fbLogEvent(EVENT.UE_ADVANCED_FILTERS_TOGGLE, {
                          screen: SCREEN.SEARCH,
                          tab: TAB.NEW,
                          mode: isAdvancedFilterToggledOn
                            ? MODE.ADVANCED_FILTERS_OFF
                            : MODE.ADVANCED_FILTERS_ON,
                        });
                      }}
                      value={isAdvancedFilterToggledOn}
                    />
                  </div>

                  <button
                    className="Expand_Icon_Container"
                    onClick={() => {
                      handleClickExpandAdvance();
                    }}
                  >
                    <img src="/oo/assets/icon/right-arrow.png" alt="Expand-Icon" className="ml-3" />
                  </button>
                </div>
                <CustomModal
                  {...advanceFilterModalProps}
                  title={accord?.ACCORD_TITLE}
                  buttons={[
                    {
                      title: CANCEL_TEXT,
                      type: 'secondary-cta-btn',
                      onClick: () => {
                        handleCancelAdvanceSearch();
                      },
                    },
                    {
                      title: 'Accept',
                      type: `primary-cta-btn${maxMilesError ? ' disabled' : ''}`,
                      onClick: () => {
                        handleAcceptAdvanceSearch();
                      },
                    },
                  ]}
                >
                  <AdvanceSearchComponent
                    content={accord?.ACCORD_CONTENT}
                    userAlteredData={
                      isEmptyObject(temporaryUserInteractedPayload)
                        ? userAlteredData
                        : temporaryUserInteractedPayload
                    }
                    handleChange={handleChange}
                    isAdvanceSearchFilter={true}
                    maxMilesError={maxMilesError}
                  />
                </CustomModal>
              </div>
            );
          })}
          <div className="Action_Section">
            {isMobile ? (
              <>
                <div>
                  <span>Add as Favorite</span>
                </div>
                <div
                  onClick={() => {
                    handleFavorite();
                  }}
                >
                  <img
                    src={`/oo/assets/icon/star${isFavorite ? '-color' : ''}.svg`}
                    alt="Favorite icon"
                  />
                </div>
              </>
            ) : (
              <>
                <div onClick={handleClearAll}>
                  <img src="/oo/assets/icon/Delete_bin.svg" alt="Clear icon" />

                  <span>Clear</span>
                </div>
                <div
                  onClick={() => {
                    handleFavorite();
                  }}
                >
                  <img
                    src={`/oo/assets/icon/star${isFavorite ? '_border_color' : ''}.svg`}
                    alt="Favorite icon"
                  />

                  <span>{isFavorite ? 'Unfavorite' : 'Favorite'}</span>
                </div>
                <div>
                  <button
                    className="primary-cta-btn"
                    data-testid="Search"
                    onClick={handleResultsClick}
                  >
                    Search
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {!isMobile && showSearchResult && (
        <div className="Result_Wrapper">
          <ResultContainer
            additionalClass="Result_Section"
            label="Results"
            totalRecords={searchResultData?.totalRecords}
            loadPostings={searchResultData?.loadPostings}
            isStateSelectionEnabled={isStateSelectionEnabled}
            isNewSearch={true}
          />
        </div>
      )}
      {!isMobile && searchById && (
        <div className="Result_Wrapper">
          <ResultContainer
            additionalClass="Result_Section"
            label="Results"
            isNewSearch={true}
            loadPostings={loadByIdData}
          />
        </div>
      )}
      {isMobile && (
        <div className="New_Search_Footer">
          <button onClick={handleResultsClick} className="primary-cta-btn">
            See Results
          </button>
        </div>
      )}
    </div>
  );
};
export default NewSearchContainer;
